import React, { useState, useEffect } from "react";
import {
  Search,
  X,
  BadgeCheck,
  User,
  Car,
  Home,
  Phone,
  Copy,
  StickyNote,
  Pen,
  Check,
  Trash2,
  Info,
  AlertTriangle,
  Mic,
} from "lucide-react";

export function BottomSearch({ value, onChange, onSearch, activeField, setActiveField }) {
  const active = activeField === "globalSearchInput";
  return (
    <div className="fixed bottom-12 inset-x-0 z-40 px-4">
      <div className="max-w-md mx-auto bg-slate-800/90 backdrop-blur-md border border-slate-700 p-2.5 rounded-2xl shadow-2xl flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-brand-400">
            <Search size={12} />
          </span>
          <input
            type="text"
            id="globalSearchInput"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onClick={() => setActiveField("globalSearchInput")}
            onFocus={() => setActiveField("globalSearchInput")}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
            placeholder="İsim veya plaka yaz / seslen..."
            className={`w-full bg-slate-900 border ${
              active ? "border-brand-500 ring-2 ring-brand-500/30" : "border-slate-700"
            } focus:border-brand-500 rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-400 focus:outline-none transition-colors`}
          />
        </div>
        <button
          onClick={onSearch}
          className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md flex items-center gap-1.5 transition-colors active:scale-95 shrink-0"
        >
          <Search size={12} /> Arama
        </button>
      </div>
    </div>
  );
}

export function Footer({ animKey }) {
  return (
    <footer className="bg-slate-900 border-t border-slate-800 py-2.5 px-4 text-center shrink-0 overflow-hidden relative z-30">
      <div
        key={animKey}
        className="max-w-md mx-auto text-[11px] text-slate-400 font-medium flex items-center justify-center gap-1.5 w-full animate-slide-blink"
      >
        <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0"></span>
        <span>
          Bu uygulama <span className="text-white font-bold">Ahmet Soylu</span> tarafından 2026 yılında tasarlanmıştır.
        </span>
      </div>
    </footer>
  );
}

export const SearchResultModal = React.forwardRef(function SearchResultModal(
  { modal, noteListeningId, registerSetters, setActiveField, onClose, onDelete, onCopy, onNoteSave },
  ref
) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!modal.open) setEditingId(null);
  }, [modal.open]);

  useEffect(() => {
    if (editingId != null) {
      registerSetters({
        ["noteInput_" + editingId]: (v, opts) =>
          setDraft((prev) => (opts && opts.append ? (prev ? prev + " " : "") + v : v)),
      });
    }
  }, [editingId, registerSetters]);

  const enableEdit = (item) => {
    setEditingId(item.id);
    setDraft(item.infoNote || "");
    setActiveField("noteInput_" + item.id);
  };

  const saveNote = (id) => {
    onNoteSave(id, draft.trim());
    setEditingId(null);
  };

  React.useImperativeHandle(
    ref,
    () => ({
      saveNoteByField: (fieldId) => {
        const id = Number(String(fieldId).split("_")[1]);
        if (editingId === id) saveNote(id);
      },
    }),
    [editingId, draft]
  );

  if (!modal.open) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md z-[105] flex flex-col items-center justify-center p-4">
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-slate-800 text-slate-300 hover:text-white border border-slate-700 flex items-center justify-center shadow-lg transition-colors active:scale-95 z-10"
      >
        <X size={18} />
      </button>

      <div className="w-full max-w-md text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-500/20 text-brand-400 mb-3 border border-brand-500/30">
          <BadgeCheck size={30} />
        </div>
        <h2 className="text-2xl font-bold text-white">Sakin Kartı</h2>
        <p className="text-xs text-slate-400 mt-1">{modal.subtitle}</p>
      </div>

      <div className="w-full max-w-md space-y-4 overflow-y-auto max-h-[65vh] pb-10 px-1">
        {modal.results.map((item) => (
          <div
            key={item.id}
            className="bg-slate-800 border-2 border-brand-500/50 rounded-3xl p-6 flex flex-col gap-5 relative overflow-hidden animate-pop-in mt-4 shadow-xl"
          >
            <div className="absolute top-0 right-0 bg-brand-600 text-white text-base px-6 py-2.5 rounded-bl-2xl font-bold shadow-md tracking-wider z-10 flex items-center gap-1.5">
              <span className="text-xs font-sans opacity-80 uppercase tracking-widest">Plaka</span>
              <span className="font-mono">{item.plateNo}</span>
            </div>

            <div className="flex items-center gap-4 pt-4 mt-2">
              <div className="w-16 h-16 rounded-2xl bg-slate-700 text-brand-400 flex items-center justify-center shadow-inner shrink-0 border border-slate-600">
                <User size={30} />
              </div>
              <div className="flex-1 pr-16 text-left">
                <h4 className="font-bold text-white text-xl leading-tight">{item.fullName}</h4>
                <div className="inline-block mt-1.5 text-xs bg-brand-500/20 border border-brand-500/30 text-brand-400 px-3 py-1 rounded-full font-medium shadow-sm">
                  {item.relationDegree}
                </div>
              </div>
            </div>

            <div className="bg-slate-900/80 p-4 rounded-2xl border border-slate-700 text-sm space-y-3 shadow-inner">
              <div className="flex items-center justify-between text-slate-300">
                <span className="text-slate-500 flex items-center gap-2">
                  <Car size={14} className="w-4" /> Araç:
                </span>
                <span className="font-bold text-white text-right">{item.carModel}</span>
              </div>
              <div className="h-px w-full bg-slate-800"></div>
              <div className="flex items-center justify-between text-slate-300">
                <span className="text-slate-500 flex items-center gap-2">
                  <Home size={14} className="w-4" /> Daire:
                </span>
                <span className="font-bold text-white text-lg">{item.aptNo}</span>
              </div>
              <div className="h-px w-full bg-slate-800"></div>
              <div className="flex items-center justify-between text-slate-300">
                <span className="text-slate-500 flex items-center gap-2">
                  <Phone size={14} className="w-4" /> Tel:
                </span>
                <span className="font-bold text-brand-400 text-lg flex items-center gap-2">
                  {item.phoneNo}
                  <button
                    onClick={() => onCopy(item.phoneNo)}
                    className="w-8 h-8 flex items-center justify-center bg-brand-500/10 hover:bg-brand-500/20 text-brand-500 rounded-lg border border-brand-500/30 transition-colors active:scale-95"
                    title="Kopyala"
                  >
                    <Copy size={13} />
                  </button>
                </span>
              </div>
              <div className="h-px w-full bg-slate-800"></div>

              <div className="flex flex-col gap-2 pt-1">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-bold text-xs flex items-center gap-1">
                    <StickyNote size={12} /> Bilgi Notu
                  </span>
                  <button
                    onClick={() => enableEdit(item)}
                    className="text-[10px] text-brand-400 hover:text-brand-300 px-2 py-1 bg-brand-500/10 rounded-md transition-colors active:scale-95 flex items-center gap-1"
                  >
                    <Pen size={10} /> Manuel Ekle
                  </button>
                </div>

                {noteListeningId === item.id ? (
                  <p className="text-left p-3 rounded-xl min-h-[3rem] text-xs bg-brand-500/20 text-brand-400 border border-brand-500/50 flex items-center">
                    <Mic size={14} className="animate-pulse mr-2 shrink-0" /> Dinleniyor, lütfen notunuzu söyleyin...
                  </p>
                ) : editingId === item.id ? (
                  <>
                    <textarea
                      id={"noteInput_" + item.id}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={3}
                      placeholder="Bilgi notu yazın..."
                      className="w-full bg-slate-950 border border-brand-500 rounded-xl p-3 text-white text-xs focus:outline-none"
                    />
                    <button
                      onClick={() => saveNote(item.id)}
                      className="w-full bg-brand-600 hover:bg-brand-500 text-white font-bold py-2 rounded-xl mt-1 transition-colors active:scale-95 text-xs shadow-md flex items-center justify-center gap-1"
                    >
                      <Check size={12} /> Notu Kaydet
                    </button>
                  </>
                ) : (
                  <p className="text-slate-300 text-left bg-slate-800 p-3 rounded-xl min-h-[3rem] text-xs border border-transparent">
                    {item.infoNote || "Bilgi notu eklenmemiş."}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end mt-1">
              <button
                onClick={() => onDelete(item.id)}
                className="bg-red-500 text-white hover:bg-red-600 px-5 py-3 rounded-xl border border-red-400 font-bold flex items-center gap-2 transition-colors shadow-lg active:scale-95 w-full justify-center"
              >
                <Trash2 size={18} /> Kaydı Sil
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export function CustomModal({ modal, onConfirm }) {
  if (!modal.open) return null;

  const iconMap = {
    success: { cls: "bg-brand-500/20 text-brand-400", icon: <Check size={20} /> },
    error: { cls: "bg-red-500/20 text-red-400", icon: <X size={20} /> },
    info: { cls: "bg-blue-500/20 text-blue-400", icon: <Info size={20} /> },
    confirm: { cls: "bg-red-500/20 text-red-400", icon: <AlertTriangle size={20} /> },
  };
  const conf = iconMap[modal.type] || iconMap.info;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 w-full max-w-xs rounded-2xl p-5 text-center shadow-2xl">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${conf.cls}`}>
          {conf.icon}
        </div>
        <h3 className="text-white font-bold text-base mb-1">{modal.title}</h3>
        <p className="text-slate-400 text-xs mb-4 whitespace-pre-line">{modal.desc}</p>
        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2.5 rounded-xl text-xs shadow-md transition-colors"
          >
            {modal.confirmText || "Tamam"}
          </button>
        </div>
      </div>
    </div>
  );
}
